import {HoistModel, LoadSpec, managed, TaskObserver, XH} from '@xh/hoist/core';
import {UserName} from '../../../../core/DataTypes';
import {Distribution, Portfolio, Security, TransactionBatch} from '../../../data/DataTypes';
import {GridModel} from '@xh/hoist/cmp/grid';
import {GroupingChooserModel} from '@xh/hoist/cmp/grouping';
import {Cube, View} from '@xh/hoist/data';
import {AddDistributionDialogModel} from './AddDistributionDialogModel';
import {endsWith, isEmpty} from 'lodash';
import {getDefaultOptions} from '../../../../core/ServiceUtils';
import {
    allDistributionsFields,
    amountCol,
    buttonsCol,
    distroActionCol,
    entityCol,
    idCol,
    portfolioIdCol,
    portfolioNameCol,
    postedCol,
    securityIdCol,
    securityNameCol,
    treeCol,
    updatedByCol,
    updatedCol
} from './DistributionsFields';

export class DistributionsModel extends HoistModel {
    private users: Map<number, UserName> = new Map();
    private portfolios: Map<number, Portfolio> = new Map();
    private securities: Map<number, Security> = new Map();
    apiTask = TaskObserver.trackLast();
    @managed gridModel: GridModel;
    @managed groupingChooserModel: GroupingChooserModel;
    @managed addDistributionDialog = new AddDistributionDialogModel();
    @managed private cube: Cube;
    @managed private cubeView: View;
    override persistWith = {prefKey: 'realEstateTabState'};

    constructor() {
        super();

        this.setUpCube();
    }

    override async doLoadAsync(loadSpec: LoadSpec) {
        if (loadSpec.isRefresh || loadSpec.loadNumber === 0) {
            await this.initDomainDataAsync().linkTo(this.apiTask);
        }

        const distributions = await this.fetchDistributionsAsync().linkTo(this.apiTask);

        this.cube.loadDataAsync(distributions).linkTo(this.apiTask);
    }

    get isGrouped(): boolean {
        return !isEmpty(this.groupingChooserModel?.value);
    }

    expand() {
        this.gridModel.expandAll();
    }

    collapse() {
        this.gridModel.collapseAll();
    }

    ungroup() {
        this.groupingChooserModel.setValue([]);
    }

    refresh() {
        this.loadAsync({isRefresh: true});
    }

    async addNew() {
        const distributions = await this.addDistributionDialog.openAsync();
        if (!distributions) return;

        const changes: TransactionBatch = {
            added: distributions.map(d => ({...d, id: 0}))
        };

        try {
            await this.saveDistributionsAsync(changes).linkTo(this.apiTask);
            this.loadAsync();
        } catch (e) {
            XH.handleException(e, {title: 'Error saving distributions'});
        }
    }

    async delete(data) {
        const changes: TransactionBatch = {
            removed: [{...data, id: parseInt(data.cubeLabel)}]
        };

        try {
            await this.saveDistributionsAsync(changes).linkTo(this.apiTask);
            this.loadAsync();
        } catch (e) {
            XH.handleException(e, {title: 'Error deleting distribution'});
        }
    }

    async update(data) {
        const changes: TransactionBatch = {
            modified: [{...data, id: parseInt(data.cubeLabel)}]
        };

        try {
            await this.saveDistributionsAsync(changes).linkTo(this.apiTask);
            this.loadAsync();
        } catch (e) {
            XH.handleException(e, {title: 'Error updating distribution'});
        }
    }

    private setUpCube() {
        this.cube = new Cube({
            fields: allDistributionsFields,
            processRawData: raw => {
                const portfolio = this.portfolios.get(raw.portfolioId);
                return {
                    ...raw,
                    entity: this.users.get(portfolio.userId).username,
                    portfolioName: portfolio.portfolioName,
                    securityName: this.securities.get(raw.securityId)?.name
                };
            }
        });

        this.groupingChooserModel = new GroupingChooserModel({
            dimensions: this.cube.dimensions,
            persistWith: {...this.persistWith, persistFavorites: true},
            initialValue: [],
            initialFavorites: [['entity'], ['action']],
            allowEmpty: true,
            commitOnChange: true
        });

        this.gridModel = this.createGridModel();

        this.cubeView = this.cube.createView({
            query: {
                dimensions: this.groupingChooserModel.value,
                includeLeaves: true
            },
            stores: this.gridModel.store
        });

        this.cube._connectedViews.add(this.cubeView);

        this.addReaction({
            track: () => this.groupingChooserModel.value,
            run: (newValue, oldValue) => {
                this.setVisibleColumns(newValue, oldValue);
                this.cubeView.updateQuery({dimensions: newValue});
            },
            fireImmediately: true
        });
    }

    private setVisibleColumns(newDimensions: string[], oldDimensions: string[]) {
        if (isEmpty(newDimensions)) {
            this.gridModel
                .getLeafColumns()
                .filter(c => !c.appData.alwaysHide)
                .forEach(c => this.gridModel.showColumn(c.colId));
            this.gridModel.hideColumn('cubeLabel');
        } else {
            if (!isEmpty(oldDimensions)) oldDimensions.forEach(dim => this.gridModel.showColumn(dim));
            this.gridModel.showColumn('cubeLabel');
            newDimensions.forEach(dim => this.gridModel.hideColumn(dim));
        }
    }

    private createGridModel(): GridModel {
        return new GridModel({
            // Rows are edited and reverted in place, which a projectionOnly store does not allow
            store: {projectionOnly: false},
            treeMode: true,
            treeStyle: 'borders',
            selModel: 'disabled',
            cellBorders: true,
            colDefaults: {
                hideable: false,
                movable: false
            },
            columns: [
                treeCol(this),
                idCol,
                portfolioIdCol,
                securityIdCol,
                entityCol,
                portfolioNameCol,
                securityNameCol,
                postedCol,
                amountCol,
                distroActionCol,
                updatedCol,
                updatedByCol,
                {field: 'ownerUserId', hidden: true, appData: {alwaysHide: true}},
                {field: 'timestamp', hidden: true, appData: {alwaysHide: true}},
                buttonsCol(this)
            ],
            rowClassFn: record => {
                if (!endsWith(String(record.id), record.data.cubeLabel)) {
                    return 'distribution-group-row';
                }
            }
        });
    }

    private fetchDistributionsAsync(): Promise<Distribution[]> {
        return XH.fetchService.getJson({
            url: `${XH.getConf('svcApiUrl')}/realestate/distributions`,
            ...getDefaultOptions()
        });
    }

    private saveDistributionsAsync(changes: TransactionBatch): Promise<void> {
        return XH.postJson({
            url: `${XH.getConf('svcApiUrl')}/realestate/distributions/save`,
            body: changes,
            ...getDefaultOptions()
        });
    }

    private async initDomainDataAsync() {
        const securities = await XH.secMasterService.searchSecuritiesAsync('%');
        const portfolios = await XH.fetchService.getJson({
            url: `${XH.getConf('svcApiUrl')}/portfolios/all`,
            ...getDefaultOptions()
        });
        const users = await XH.fetchService.fetchJson({
            url: `${XH.getConf('svcApiUrl')}/users/names`,
            ...getDefaultOptions()
        });

        this.securities = new Map(securities.map(s => [s.id, s]));
        this.portfolios = new Map(portfolios.map(p => [p.id, p]));
        this.users = new Map(users.map(u => [u.id, u]));
    }
}
